import { lazy, RefObject, useRef } from 'react';
import * as React from 'react';

import MultiraceLogo from '@/components/elements/Logo/MultiraceLogo';
import CarryOver from '@/components/layouts/Guide/Voting/CarryOver';
import { MultiraceType } from '@/lib/apollo/types/gen/graphql';
import { RaceType } from '@/modelTypes';
import { GuidePageTitles } from '@/pages/Guide/types';

const MultiRandomPC = lazy(
  () => import('@/components/layouts/Guide/Voting/MultiRandomPC'),
);
const MultiRandomSP = lazy(
  () => import('@/components/layouts/Guide/Voting/MultiRandomSP'),
);
const MultiSelectPC = lazy(
  () => import('@/components/layouts/Guide/Voting/MultiSelectPC'),
);
const MultiSelectSP = lazy(
  () => import('@/components/layouts/Guide/Voting/MultiSelectSP'),
);
const VotingSelectDevice = lazy(
  () => import('@/components/layouts/Guide/Voting/SelectDevice'),
);
const SinglePC = lazy(
  () => import('@/components/layouts/Guide/Voting/SinglePC'),
);
const SingleSP = lazy(
  () => import('@/components/layouts/Guide/Voting/SingleSP'),
);

import {
  Container,
  ContentBox,
  Text,
  TextLarge,
  TextNoteList,
  TextSmall,
  Title,
  TitleSecondary,
  TitleSmall,
  TitleTertiary,
} from '../style';
import {
  HeaderBox,
  HeaderWrapper,
  Hr,
  MultiHeader,
  MultiType,
  VotingTabs,
  VotingTabsButton,
} from './style';

interface GuideVotingTemplateProps {
  raceType: RaceType;
  setRaceType: React.Dispatch<React.SetStateAction<RaceType>>;
}

const GuideVotingTemplate: React.FC<GuideVotingTemplateProps> = ({
  raceType,
  setRaceType,
}) => {
  const SingleSPRef = useRef(null);
  const SinglePCRef = useRef(null);
  const MultiRandomSPRef = useRef(null);
  const MultiRandomPCRef = useRef(null);
  const MultiSelectSPRef = useRef(null);
  const MultiSelectPCRef = useRef(null);

  const handleDeviceSelectClick = <T extends HTMLElement>(
    ref: RefObject<T>,
  ) => {
    window.scrollTo({
      top: ref.current?.offsetTop,
      behavior: 'smooth',
    });
  };

  const HeaderNotes = (
    <TextNoteList>
      <li>
        <i>※</i>投票を行うにはDMM競輪への利用登録（無料）が必要です。
      </li>
      <li>
        <i>※</i>画面のデザインは最新のものと異なる場合があります。
      </li>
    </TextNoteList>
  );

  return (
    <Container>
      <ContentBox>
        <Title>{GuidePageTitles.Voting}</Title>
        <VotingTabs>
          <VotingTabsButton
            isActive={raceType === RaceType.Singlerace}
            onClick={() => setRaceType(RaceType.Singlerace)}
          >
            競輪投票
          </VotingTabsButton>
          <VotingTabsButton
            isActive={raceType === RaceType.Multirace}
            onClick={() => setRaceType(RaceType.Multirace)}
          >
            <span>チャリロト</span>Dokanto!
          </VotingTabsButton>
        </VotingTabs>
        {raceType === RaceType.Singlerace && (
          <>
            <TextLarge>競輪投票の投票ページの使い方をご案内します。</TextLarge>
            {HeaderNotes}
            <VotingSelectDevice
              handleClickSP={() => handleDeviceSelectClick(SingleSPRef)}
              handleClickPC={() => handleDeviceSelectClick(SinglePCRef)}
            />
          </>
        )}
        {raceType === RaceType.Multirace && (
          <>
            <TextLarge>
              チャリロトとDokanto!は、複数レースの1着を全て当てると的中となる重勝式の投票方法です。
              <br />
              組番の選び方は「<b>クイックピック式</b>」と「<b>セレクト式</b>
              」の2種類あります。
            </TextLarge>
            {HeaderNotes}
            <HeaderWrapper>
              <TitleSecondary>種類と購入方法</TitleSecondary>
              <HeaderBox>
                <TextSmall>初心者にオススメ！</TextSmall>
                <TitleSmall>クイックピック式</TitleSmall>
                <Text>
                  コンピュータが自動で組番を発行します。1口200円から購入でき、購入口数を選ぶだけでOKなので競輪の知識がなくても投票できます。
                  <br />
                  キャリーオーバーで最大12億円のチャンス！
                </Text>
                <MultiType>
                  <dt>
                    <MultiraceLogo
                      multiraceType={MultiraceType.Chariloto}
                      height="24px"
                    />
                    チャリロト
                  </dt>
                  <dd>
                    1つの競輪場で行われるレースの
                    <strong>後半7レースの1着を当てる</strong>。
                  </dd>
                  <dt>
                    <MultiraceLogo
                      multiraceType={MultiraceType.Dokanto7}
                      height="20px"
                    />
                    Dokanto!7
                  </dt>
                  <dd>
                    1つの競輪場で行われるレースの
                    <strong>後半7レースの1着を当てる</strong>。
                  </dd>
                  <dt>
                    <MultiraceLogo
                      multiraceType={MultiraceType.Dokanto4two}
                      height="20px"
                    />
                    Dokanto!4two
                  </dt>
                  <dd>
                    1つの競輪場で行われるレースの
                    <strong>後半4レースの1着・2着（順不同）を当てる</strong>。
                  </dd>
                </MultiType>
                <VotingSelectDevice
                  handleClickSP={() =>
                    handleDeviceSelectClick(MultiRandomSPRef)
                  }
                  handleClickPC={() =>
                    handleDeviceSelectClick(MultiRandomPCRef)
                  }
                />
              </HeaderBox>
              <HeaderBox>
                <TitleSmall>セレクト式</TitleSmall>
                <Text>
                  各レースの1着を自分で予想して組番を選択します。1口100円から購入でき、自分で予想して高配当をゲットしたい！
                  そんな人にオススメです。
                </Text>
                <MultiType>
                  <dt>
                    <MultiraceLogo
                      multiraceType={MultiraceType.CharilotoSelect}
                      height="24px"
                    />
                    チャリロトセレクト
                  </dt>
                  <dd>
                    1つの競輪場で行われるレースの
                    <strong>後半7レースの1着を当てる</strong>。
                  </dd>
                  <dt>
                    <MultiraceLogo
                      multiraceType={MultiraceType.Chariloto5}
                      height="24px"
                    />
                    チャリロト5
                  </dt>
                  <dd>
                    1つの競輪場で行われるレースの
                    <strong>後半5レースの1着を当てる</strong>。
                  </dd>
                  <dt>
                    <MultiraceLogo
                      multiraceType={MultiraceType.Chariloto3}
                      height="24px"
                    />
                    チャリロト3
                  </dt>
                  <dd>
                    1つの競輪場で行われるレースの
                    <strong>後半3レースの1着を当てる</strong>。
                  </dd>
                </MultiType>
                <VotingSelectDevice
                  handleClickSP={() =>
                    handleDeviceSelectClick(MultiSelectSPRef)
                  }
                  handleClickPC={() =>
                    handleDeviceSelectClick(MultiSelectPCRef)
                  }
                />
              </HeaderBox>
            </HeaderWrapper>
            <TextNoteList>
              <li>
                <i>※</i>当せん者は、確定申告が必要な場合があります。
              </li>
              <li>
                <i>※</i>法律により未成年者のチケット購入はできません。
              </li>
            </TextNoteList>
          </>
        )}
      </ContentBox>
      {raceType === RaceType.Singlerace && (
        <>
          <ContentBox>
            <SingleSP refObj={SingleSPRef} />
          </ContentBox>
          <ContentBox>
            <SinglePC refObj={SinglePCRef} />
          </ContentBox>
        </>
      )}
      {raceType === RaceType.Multirace && (
        <>
          <ContentBox>
            <MultiHeader>
              <TitleTertiary>クイックピック式 投票ページの使い方</TitleTertiary>
              <TextLarge>
                クイックピック式なら、投票もカンタン！
                購入口数を選ぶだけでOKです。
              </TextLarge>
            </MultiHeader>
            <MultiRandomSP refObj={MultiRandomSPRef} />
            <Hr />
            <MultiRandomPC refObj={MultiRandomPCRef} />
          </ContentBox>
          <ContentBox>
            <MultiHeader>
              <TitleTertiary>セレクト式 投票ページの使い方</TitleTertiary>
              <TextLarge>
                対象レース数や一部表記は異なりますが、
                <b>チャリロトセレクト・チャリロト5・チャリロト3</b>
                は同様の流れで投票が行えます。
              </TextLarge>
            </MultiHeader>
            <MultiSelectSP refObj={MultiSelectSPRef} />
            <Hr />
            <MultiSelectPC refObj={MultiSelectPCRef} />
          </ContentBox>
          <ContentBox>
            <CarryOver />
          </ContentBox>
        </>
      )}
    </Container>
  );
};

export default GuideVotingTemplate;
